import React from 'react';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useTheme } from '../Theme/ThemeProvider';

interface NetworkWarningProps {
  onDismiss?: () => void;
}

/**
 * Warning banner displayed when slow network connection is detected
 * Shows above the first user message
 */
export const NetworkWarning: React.FC<NetworkWarningProps> = ({ onDismiss }) => {
  const { mode } = useTheme();

  return (
    <div
      className={`mb-4 rounded-lg border-l-4 p-4 ${
        mode === 'dark'
          ? 'bg-yellow-900/20 border-yellow-500 text-yellow-200'
          : 'bg-yellow-50 border-yellow-400 text-yellow-800'
      }`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon 
          className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
            mode === 'dark' ? 'text-yellow-500' : 'text-yellow-600'
          }`}
        />
        <div className="flex-1">
          <h3 className="font-semibold text-sm mb-1">
            Slow Network Connection Detected
          </h3>
          <p className="text-sm">
            Your current network connection may impact the performance of 3M Navigator. 
            Response times may be slower than usual.
          </p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`flex-shrink-0 rounded-md p-1 hover:bg-opacity-20 ${
              mode === 'dark'
                ? 'hover:bg-yellow-400'
                : 'hover:bg-yellow-600'
            }`}
            aria-label="Dismiss warning"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
